import {
  useEffect,
  useState,
} from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Menu,
  X,
} from 'lucide-react';

import Sidebar from './sidebar/Sidebar';

import type {
  RouteItem,
} from '../../routes/RouteConfig';

export default function ManagementLayout() {
  const [
    isDesktopSidebarOpen,
    setIsDesktopSidebarOpen,
  ] = useState(true);

  const [
    isMobileSidebarOpen,
    setIsMobileSidebarOpen,
  ] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Sidebar 활성화 메뉴 비교에 사용합니다.
   *
   * Sidebar에서는 각 RouteConfig의 fullPath와
   * 현재 location.pathname을 비교합니다.
   */
  const currentPath =
    location.pathname;

  /**
   * Sidebar에서 RouteItem 객체를 전달받아
   * 해당 route의 fullPath로 이동합니다.
   */
  const handleNavigate = (
    route: RouteItem,
  ): void => {
    navigate(route.fullPath);

    setIsMobileSidebarOpen(false);
  };

  /**
   * 모바일 사이드바가 열리면
   * 배경 페이지 스크롤을 막습니다.
   */
  useEffect(() => {
    if (!isMobileSidebarOpen) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      'hidden';

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isMobileSidebarOpen]);

  /**
   * 경로가 변경되면
   * 모바일 사이드바를 자동으로 닫습니다.
   */
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  /**
   * Escape 키로 모바일 사이드바를 닫습니다.
   */
  useEffect(() => {
    const handleKeyDown = (
      event: KeyboardEvent,
    ): void => {
      if (
        event.key === 'Escape' &&
        isMobileSidebarOpen
      ) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyDown,
    );

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown,
      );
    };
  }, [isMobileSidebarOpen]);

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] w-full overflow-hidden bg-gray-50">
      {/* 데스크톱 사이드바 */}
      <aside className="hidden h-full shrink-0 overflow-hidden md:block">
        <div className="h-full">
          <Sidebar
            isOpen={
              isDesktopSidebarOpen
            }
            currentPath={
              currentPath
            }
            onNavigate={
              handleNavigate
            }
          />
        </div>
      </aside>

      {/* 모바일 햄버거 버튼 */}
      {!isMobileSidebarOpen && (
        <button
          type="button"
          className="fixed left-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-800 shadow-md transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-main/30 md:hidden"
          onClick={() =>
            setIsMobileSidebarOpen(
              true,
            )
          }
          aria-label="메뉴 열기"
          aria-controls="mobile-sidebar"
          aria-expanded={
            isMobileSidebarOpen
          }
        >
          <Menu
            size={22}
            aria-hidden="true"
          />
        </button>
      )}

      {/* 모바일 사이드바 배경 */}
      <button
        type="button"
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isMobileSidebarOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
        onClick={() =>
          setIsMobileSidebarOpen(
            false,
          )
        }
        aria-label="메뉴 닫기"
        tabIndex={
          isMobileSidebarOpen
            ? 0
            : -1
        }
      />

      {/* 모바일 슬라이드 사이드바 */}
      <aside
        id="mobile-sidebar"
        className={`fixed left-0 top-0 z-50 h-[100dvh] w-[280px] max-w-[85vw] transform overflow-hidden bg-white shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
          isMobileSidebarOpen
            ? 'translate-x-0'
            : '-translate-x-full'
        }`}
        aria-hidden={
          !isMobileSidebarOpen
        }
      >
        <div className="relative h-full overflow-y-auto">
          {/* 모바일 닫기 버튼 */}
          <button
            type="button"
            className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-gray-500 shadow-sm transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-main/30"
            onClick={() =>
              setIsMobileSidebarOpen(
                false,
              )
            }
            aria-label="메뉴 닫기"
          >
            <X
              size={22}
              aria-hidden="true"
            />
          </button>

          <div className="h-full">
            <Sidebar
              isOpen
              currentPath={
                currentPath
              }
              onNavigate={
                handleNavigate
              }
            />
          </div>
        </div>
      </aside>

      {/* 페이지 콘텐츠 */}
      <main className="h-full min-h-0 min-w-0 flex-1 overflow-hidden bg-gray-50">
        <div className="h-full overflow-y-auto overflow-x-hidden">
          <div className="min-h-full pt-16 md:pt-0">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

